import { ArticleSection } from './models/article-section';
import { NavService } from '../../nav/service/nav.service';
import {
  AfterContentInit,
  Component,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  RESPONSE_INIT,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CommonModule,
  DatePipe,
  isPlatformBrowser,
  isPlatformServer,
  NgOptimizedImage,
} from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { CommentComponent } from './comment/comment.component';
import { DialogModule } from 'primeng/dialog';
import { FormsModule } from '@angular/forms';
import { SummaryComponent } from './summary/summary.component';
import { StickyLinksComponent } from './sticky-links/sticky-links.component';
import {
  Article,
  Section,
  TitleData,
  ArticleService,
  CommentService,
  ArticleType,
  Comment,
} from '../../admin/shared/public-api';
import { TemplateDirective } from '../../admin/templates/template.directive';
import { ArticleCardComponent } from '../../shared/components/article-card/article-card.component';
import { InnerHTMLComponent } from '../../shared/components/inner-html/inner-html.component';
import { ArticleDirective } from '../../shared/directives/article.directive';
import { DeviceService } from '../../shared/services/device.service';
import { MetaService } from '../../shared/services/meta.service';
import { JSONLDService } from '../../shared/services/jsonld.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-article',
  templateUrl: './article.component.html',
  styleUrls: ['./article.component.scss'],
  imports: [
    CommonModule,
    ArticleCardComponent,
    ArticleDirective,
    CommentComponent,
    DatePipe,
    DialogModule,
    FormsModule,
    TemplateDirective,
    NgOptimizedImage,
    InnerHTMLComponent,
    SummaryComponent,
    StickyLinksComponent,
  ],
})
export class ArticleComponent implements OnInit, OnDestroy {
  protected deviceService = inject(DeviceService);
  private readonly articleService = inject(ArticleService);
  private readonly commentService = inject(CommentService);
  private readonly navService = inject(NavService);
  private readonly metaTagService = inject(MetaService);
  private readonly titleService = inject(Title);
  private readonly JSONLDService = inject(JSONLDService);

  article = new Article();
  discoverArticles: Article[];
  showCommentModal = false;
  commentModel = new Comment();
  articleSections: ArticleSection<any>[] = [];
  articleImageSrc: string;
  articleSummaryTitles: Section<TitleData>[] = [];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.navService.setTransparentBackground();
    this.route.snapshot.params['articleSlug'];
    let articleSlug;
    if (this.route.snapshot.data && this.route.snapshot.data['name']) {
      if (this.route.snapshot.data['name'] === 'Setup') {
        articleSlug = `notre-materiel`;
      }
    }
    articleSlug = articleSlug ?? this.route.snapshot.params['articleSlug'];
    this.loadArticle(articleSlug);
  }

  ngOnDestroy(): void {
    this.metaTagService.removeTag('property="article:published_time"');
    this.metaTagService.removeTag('property="article:modified_time"');
    this.JSONLDService.removeStructuredData();
  }

  getDate(date: Date): string | null {
    const datePipe: DatePipe = new DatePipe('fr');
    return datePipe.transform(new Date(date), 'dd MMMM yyy');
  }

  loadArticle(articleSlug: string): void {
    const $article = this.articleService.getArticleBySlug(articleSlug);
    $article.subscribe((res) => {
      const article = res;
      if (isPlatformBrowser(this.platformId) && !article) {
        window.location.href = '/404';
      }

      this.articleImageSrc = `
            linear-gradient(0deg, rgba(0, 0, 0, 0.7) 22%, rgba(0, 0, 0, 0) 100%),
            url("${article.coverPicture?.src.replace(/\\/g, '/')}")
            `;
      this.articleSections = [];
      article.sections = article.sections?.sort((a, b) => a.order - b.order);
      this.article = article;
      article.sections?.forEach((x) => {
        const articleSection = new ArticleSection();
        Object.assign(articleSection, x);
        this.setCustomClass(articleSection);
        this.articleSections.push(articleSection);
        if (['main-title', 'secondary-title'].includes(x.componentName)) {
          this.articleSummaryTitles.push(x);
        }
      });

      //#region SEO

      this.JSONLDService.insertSchema(article);
      this.titleService.setTitle(
        (this.route.snapshot.data['title'] as string).replace('$replace', article.name),
      );

      const baseUrl = environment.baseUrl;

      const ogUrl =
        article.articleType === ArticleType.Setup
          ? `${baseUrl}${`materiel`}/${article.slug}`
          : `${baseUrl}${article.destination?.country}/${article.slug}`;

      this.metaTagService.updateMetaTags(
        article.seoDescription,
        article.name,
        ogUrl,
        'article',
        article.coverPicture ? `${baseUrl}${article.coverPicture.src}` : undefined,
        article.coverPicture ? article.coverPicture.width : undefined,
        article.coverPicture ? article.coverPicture.height : undefined,
        'image/jpeg',
        article.creationDate,
        article.updateDate,
      );

      //#endregion
    });

    this.articleService.getRandomArticles().subscribe((randomArticles) => {
      this.discoverArticles = randomArticles;
    });
  }

  setCustomClass(section: ArticleSection<any>): void {
    const nextSection = this.article.sections?.find((x) => x.order === section.order + 1);
    if (section.componentName === 'picture') {
      if (nextSection?.componentName === 'picture') {
        section.customClass = 'picture-margin';
      }
      return;
    }

    if (this.deviceService.isMobile() && section.componentName.includes('-title')) {
      section.customClass = 'mobile-title';
      return;
    }
  }

  getCommentsNumber(comments: Comment[]): number {
    let result = 0;
    if (comments) {
      result += comments.length;
    } else {
      return result;
    }

    for (let i = 0; i < comments.length; i++) {
      result += this.getCommentsNumber(comments[i].commentResponses);
    }

    return result;
  }

  addComment(parentCommentId: string | null = null): void {
    this.commentModel = new Comment();
    this.commentModel.articleId = this.article.id;
    this.commentModel.date = new Date();
    if (parentCommentId) {
      delete this.commentModel.articleId;
      this.commentModel.commentResponseId = parentCommentId;
    }
    this.showCommentModal = true;
  }

  saveComment(): void {
    this.commentService.createComment(this.commentModel).subscribe(() => {
      this.commentService
        .getArticleComments(this.article.id)
        .subscribe((comments) => (this.article.comments = comments));
      this.showCommentModal = false;
    });
  }
}
